import { Link } from 'gatsby';
import React from 'react';
import { ArrowRight } from 'react-feather';
import styled from '../../styled';
import { Headline } from '../Headline';
import { Reveal } from '../motion';
import { ComingSoonBadge, NewBadge, NoCodeBadge } from '../NewBadge';

const SectionWrapper = styled('section')`
  padding: 100px 24px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 960px) {
    padding: 80px 24px;
  }
`;

const SectionHeader = styled('div')`
  text-align: center;
  margin-bottom: 60px;

  @media (max-width: 960px) {
    margin-bottom: 40px;
  }
`;

const SectionDescription = styled('p')`
  font-size: 18px;
  line-height: 1.6;
  color: #4f4f4f;
  margin-top: 16px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const Grid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  background-color: #fff;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const SolutionCard = styled('div')<{ new: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ImagePlaceholder = styled('div')`
  width: 100%;
  aspect-ratio: 16 / 9;
  background-color: #e2e8f0;
  border-radius: 12px;
  margin-bottom: 8px;
`;

const SolutionTitle = styled('h3')`
  font-size: 24px;
  font-weight: 700;
  color: #0e1c2b;
  margin: 0;
`;

const SolutionDescription = styled('p')`
  font-size: 16px;
  line-height: 1.6;
  color: #4f4f4f;
  margin: 0;
`;

const PerfectFor = styled('div')`
  margin-top: 8px;
`;

const PerfectForLabel = styled('p')`
  font-size: 14px;
  font-weight: 600;
  color: #4f4f4f;
  margin: 0 0 12px 0;
`;

const TeamList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TeamItem = styled('li')`
  font-size: 14px;
  color: #4f4f4f;
  position: relative;
  padding-left: 20px;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #1890ff;
    font-weight: bold;
  }
`;

const LearnMoreLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #1890ff;
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  transition: opacity 0.2s;
  width: fit-content;
  margin-top: 8px;

  &:hover {
    opacity: 0.8;
  }
`;

type Solution = {
  title: string;
  description: string;
  teams: string[];
  image: string;
  new: boolean;
  slug: string;
  badge?: React.ReactNode;
  tiers?: { label: string; badge?: React.ReactNode }[];
};

const solutions: Solution[] = [
  {
    title: 'Unify',
    description:
      'Aggregate and unify affiliate data from 100+ networks into trusted revenue intelligence: transactions, rates, finance data, and payouts. One source of truth your team — and your AI assistants — can query directly.',
    teams: [
      'Centralized insights and analytics',
      'Ask the Agent or your MCP client instead of building reports',
      'Confident financial reconciliation',
      'Modern APIs to power linking, creator analytics, user attribution and payouts',
      'Robust data warehouse compatibility',
      'To power custom, native commerce experiences'
    ],
    image: '/images/products-unify.png',
    new: false,
    slug: 'unify',
    badge: <NoCodeBadge />
  },
  {
    title: 'Optimize',
    description:
      'The content optimization solution built for editorial teams. Tie clicks and revenue to content, placements, traffic sources, and offsite distribution — and see exactly where revenue is won, lost, and ready to scale.',
    teams: [
      'On-page analytics and heatmaps for commerce content',
      'Offsite, email, and syndication tracking',
      'Custom dimensions that map revenue to your own taxonomy',
      'Paid marketing integrations like Google Ads and Facebook Ads',
      'Revenue by traffic source, including LLMs',
      'Agent analytics on the content you already monetize'
    ],
    image: '/images/products-optimize.png',
    new: false,
    slug: 'optimize'
  },
  {
    title: 'Amplify',
    description:
      'The campaigns and Yield Optimizer solution. AI analyzes your portfolio for incremental revenue, drafts the campaigns, and your team reviews and launches — sponsorships without the operational drag.',
    teams: [
      'New revenue without new inventory',
      'AI-powered campaign suggestions, ready to review',
      'Campaign infrastructure that supports scale',
      'Live insights to adjust mid-campaign'
    ],
    image: '/images/products-amplify.png',
    new: true,
    slug: 'amplify',
    tiers: [
      { label: 'Sponsored content campaigns', badge: <NoCodeBadge /> },
      { label: 'Agentic link swap campaigns' },
      { label: 'AI visibility campaigns', badge: <ComingSoonBadge /> }
    ]
  }
];

const TierList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TierItem = styled('li')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #0e1c2b;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
`;

export const PublisherSolutionsSection = () => {
  return (
    <SectionWrapper id="publisher-solutions">
      <SectionHeader>
        <Reveal>
          <Headline component="h2">Publisher Solutions</Headline>
          <SectionDescription>
            Grounded in the use cases publishers run today — unified data,
            editorial optimization, and campaigns — with AI working across all
            three
          </SectionDescription>
        </Reveal>
      </SectionHeader>
      <Grid>
        {solutions.map((solution, index) => (
          <Reveal key={solution.title} delay={index * 120}>
            <SolutionCard new={solution.new}>
              {solution.image ? (
                <img
                  src={solution.image}
                  alt={solution.title}
                  style={{ width: '100%', height: 'auto' }}
                />
              ) : (
                <ImagePlaceholder />
              )}
              <Headline
                component="h3"
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                {solution.title}
                {solution.new && <NewBadge>New</NewBadge>}
                {solution.badge}
              </Headline>
              <SolutionDescription>{solution.description}</SolutionDescription>
              {solution.tiers && (
                <TierList>
                  {solution.tiers.map((tier) => (
                    <TierItem key={tier.label}>
                      <span>{tier.label}</span>
                      {tier.badge}
                    </TierItem>
                  ))}
                </TierList>
              )}
              <PerfectFor>
                <PerfectForLabel>Built for teams who want:</PerfectForLabel>
                <TeamList>
                  {solution.teams.map((team) => (
                    <TeamItem key={team}>{team}</TeamItem>
                  ))}
                </TeamList>
              </PerfectFor>
              <LearnMoreLink to={`/product/${solution.slug}/`}>
                Learn more <ArrowRight size={18} />
              </LearnMoreLink>
            </SolutionCard>
          </Reveal>
        ))}
      </Grid>
    </SectionWrapper>
  );
};
